<script setup lang="ts">
  import {
    breakpointsTailwind,
    useBreakpoints,
    useTimeoutFn,
  } from '@vueuse/core';
  import {
    geocodeByPlaceId,
    usePlacesAutocomplete,
  } from 'vue-use-places-autocomplete';
  import { useUi } from '@/composition/use-ui';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import {
    reactive,
    onMounted,
    ref,
    watch,
    computed,
    getCurrentInstance,
    shallowRef,
    onBeforeMount,
  } from 'vue';
  import { config } from 'vue-gtag';
  import { IDeliveryInfo } from '@/interfaces/IDeliveryInfo';
  import { AlertType } from '@/interfaces/IAlert';
  import * as gmap from 'gmap-vue/dist/gmap-vue';
  import { IGmapPlace } from '@/interfaces/IGmapPlace';
  import { LMap, LMarker, LTileLayer } from 'vue2-leaflet';

  const restaurantStore = useRestaurantStore();
  const breakpoints = useBreakpoints(breakpointsTailwind);
  const emits = defineEmits(['close']);
  const ui = useUi();

  const { proxy } = getCurrentInstance();

  const props = defineProps<{
    deliveryInfo?: IDeliveryInfo;
  }>();

  const locationData = reactive<IDeliveryInfo>({
    address: '',
    address2: '',
    addressComment: '',
    lat: undefined as number | undefined,
    lng: undefined as number | undefined,
  });

  onMounted(() => {
    if (props.deliveryInfo) {
      for (const key in props.deliveryInfo) {
        if (key in locationData) {
          // @ts-ignore
          locationData[key] = props.deliveryInfo[key];
        }
      }
    }
  });

  /*const script = useScriptGoogleMaps({
    apiKey: config.public.googleApiKey,
  });

  const waitForGoogleMaps = () =>
    new Promise<void>((resolve, reject) => {
      const checkInterval = 100;
      const maxAttempts = 50;
      let attempts = 0;

      const checkGoogleMaps = () => {
        attempts++;
        if (window.google && window.google.maps && window.google.maps.places) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Timeout waiting for google.maps.places'));
        } else {
          useTimeoutFn(checkGoogleMaps, checkInterval);
        }
      };

      script.onLoaded(() => checkGoogleMaps());
      script.onError((error: any) => reject(error));
    });*/

  const gmapApi = shallowRef<any | null>(null);
  const autocompleteService = shallowRef<any | null>(null);
  async function loadGmapApi() {
    try {
      // @ts-ignore
      await proxy.$gmapApiPromiseLazy();
      console.log('Google Maps script loaded and places API is available');
      gmapApi.value = gmap.gmapApi();
      autocompleteService.value = new gmapApi.value.maps.places.AutocompleteService();
    } catch (error) {
      console.error('Error loading Google Maps script or places API:', error);
      ui.showToast(AlertType.Error, 'Error cargando Google Maps');
      emits('close');
    }
  }

  onBeforeMount(() => {
    loadGmapApi();
  });

  const query = ref('');

  const suggestions = ref(null);
  const loadingSuggestions = ref(false);

  watch(query, async (newValue) => {
    if (!newValue) {
      suggestions.value = [];
      return;
    }
    const api = autocompleteService.value;

    try {
      loadingSuggestions.value = true;
      const results = await new Promise((resolve, reject) => {
        api.getPlacePredictions(
          {
            input: newValue,
            componentRestrictions: {
              country: restaurantStore.restaurant!.country,
            },
          },
          (results, status) => {
            if (status === gmapApi.value.maps.places.PlacesServiceStatus.OK) {
              resolve(results);
            } else {
              reject(new Error('Error fetching suggestions'));
            }
          }
        );
      });
      //console.log('Suggestions', results);
      suggestions.value = results as IGmapPlace[];
    } catch (error) {
      console.error('Error fetching suggestions', error);
      ui.showToast(AlertType.Error, 'Error buscando direcciones');
    } finally {
      loadingSuggestions.value = false;
    }
  });

  /* const { suggestions, loading: loadingSuggestions } = usePlacesAutocomplete(
    query,
    {
      debounce: 500,
      minLengthAutocomplete: 3,
      autocompletionRequest: {
        componentRestrictions: {
          country: restaurantStore.restaurant!.country,
        },
      },
    }
  );

  const selectedLocation = ref<
    | ReturnType<typeof usePlacesAutocomplete>['suggestions']['value'][0]
    | { description: string }
    | null
  >(null); */
  const selectedLocation = ref<IGmapPlace | null>(null);

  async function geoLocate() {
    if (!selectedLocation.value || !('place_id' in selectedLocation.value))
      return;

    try {
      const results = await geocodeByPlaceId(selectedLocation.value.place_id);
      const { lat, lng } = results[0].geometry.location;
      locationData.lat = lat();
      locationData.lng = lng();
    } catch (error) {
      console.error('Error geolocating', error);
      ui.showToast(
        AlertType.Error,
        'Error geolocalizando, ubique su dirección en el mapa'
      );
    }
  }

  watch(selectedLocation, async (newValue) => {
    if (newValue) {
      query.value = '';
      if ('structured_formatting' in newValue)
        locationData.address = newValue.structured_formatting.main_text;
      // @ts-ignore
      else locationData.address = newValue.description;
      await geoLocate();
    }
  });

  const mapCenter = computed(() => {
    return {
      lat: locationData.lat || restaurantStore.selectedBranch!.lat || 0,
      lng: locationData.lng || restaurantStore.selectedBranch!.lon || 0,
    };
  });

  function onMapClick(data: {
    latlng: { lat: number; lng: number };
    layerPoint: { x: number; y: number };
    containerPoint: { x: number; y: number };
    originalEvent: MouseEvent;
  }) {
    locationData.lat = data.latlng.lat;
    locationData.lng = data.latlng.lng;
  }

  const addressComplete = computed(() => {
    return locationData.lat && locationData.lng && locationData.address;
  });

  function manualInput() {
    locationData.lat = mapCenter.value.lat;
    locationData.lng = mapCenter.value.lng;
  }

  function save() {
    if (!addressComplete.value) {
      ui.showToast(
        AlertType.Error,
        'Escriba la dirección exacta y ubíquela en el mapa'
      );
      return;
    }
    proxy.$parent.$emit('save', locationData);
  }
</script>

<template>
  <v-card class="choose-address-modal tw-flex tw-flex-col">
    <v-card-title class="tw-flex tw-items-center tw-justify-between">
      <h3>Elegir dirección</h3>
      <v-btn icon @click="emits('close')" class="close-button" text>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="content tw-flex-1 tw-min-h-1 tw-overflow-y-auto">
      <v-form class="tw-space-y-4" @submit.prevent>
        <template v-if="!(locationData.lat || locationData.lng)">
          <v-autocomplete
            v-model="selectedLocation"
            :items="suggestions"
            :loading="loadingSuggestions"
            :search-input.sync="query"
            item-text="description"
            label="Buscar dirección"
            placeholder="Buscar dirección"
            prepend-icon="mdi-magnify"
            clearable
            return-object
            hide-details
            hide-no-data
            autofocus
            :menu-props="{ top: true, offsetY: true, origin: 'bottom left' }"
          ></v-autocomplete>
          <div class="optional-action">
            <span class="or">o</span>
            <v-btn
              color="primary"
              class="tw-rounded-lg"
              depressed
              outlined
              @click="manualInput"
            >
              <v-icon left>mdi-map-marker-plus</v-icon>
              Ingresar ubicación manualmente
            </v-btn>
          </div>
        </template>
        <template v-else>
          <v-btn
            color="primary"
            class="tw-rounded-lg"
            depressed
            outlined
            small
            @click="locationData.lat = locationData.lng = undefined"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Buscar otra dirección
          </v-btn>
          <v-text-field
            v-model="locationData.address"
            label="Dirección exacta"
            placeholder="Piso, departamento, etc."
            hide-details
            required
          ></v-text-field>

          <v-text-field
            v-model="locationData.address2"
            label="Piso, departamento, etc."
            placeholder="Piso, departamento, etc."
            hide-details
          ></v-text-field>

          <v-text-field
            v-model="locationData.addressComment"
            label="Comentario"
            placeholder="Comentario"
            hide-details
          ></v-text-field>

          <div class="map-container tw-space-y-2">
            <div class="tw-mb-2">
              <div class="tw-font-medium">Ubicación exacta</div>
              <div class="tw-text-sm tw-text-gray-500">
                Si la ubicación no es correcta, toque en el mapa para ajustarla.
              </div>
            </div>

            <LMap
              style="height: 200px"
              :zoom="15"
              :center="[mapCenter.lat, mapCenter.lng]"
              :use-global-leaflet="false"
              @click="onMapClick"
            >
              <LTileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&amp;copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                layer-type="base"
                name="OpenStreetMap"
              />
              <LMarker :lat-lng="[mapCenter.lat, mapCenter.lng]" />
            </LMap>
          </div>
        </template>
      </v-form>
    </v-card-text>

    <v-card-actions class="tw-flex tw-items-center tw-justify-end tw-space-x-2">
      <v-btn text large @click="emits('close')">
        <v-icon left>mdi-close</v-icon>
        Cancelar
      </v-btn>
      <v-btn color="info" large @click="save" :disabled="!addressComplete">
        <v-icon left>mdi-content-save</v-icon>
        Guardar
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<style scoped lang="scss">
  .choose-address-modal {
    .content {
      @apply tw-flex tw-flex-col tw-w-full tw-divide-y tw-divide-dashed tw-divide-gray-200;

      & > * {
        @apply tw-py-3;
      }

      .optional-action {
        @apply tw-flex tw-flex-col tw-items-center tw-space-y-2;
      }

      .map-container {
        @apply tw-space-y-2;

        .map-notes {
          @apply tw-text-sm tw-text-gray-500;
        }
      }
    }
  }
</style>
